import { memo } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import remarkGfm from "remark-gfm";

/**
 * A read-only, self-contained GitHub-flavored markdown renderer. Output is styled
 * by `.chat-md` / `.chat-code` in `shared/styles/chat-prose.css` (tokenized,
 * monochrome). Fenced code keeps the monochrome `.chat-code` frame and `hljs`
 * weight/italic highlighting; links open safely in a new tab.
 *
 * This is the shared primitive — deliberately free of chat concerns (citations,
 * Canvas, message scope). Feature surfaces that just need to display markdown
 * (skill reference files, docs, previews) use this; the chat assistant keeps its
 * own richer renderer.
 */
const components: Components = {
  pre({ children }) {
    return (
      <div className="chat-code my-3 overflow-hidden rounded-md border border-line-strong bg-paper shadow-[var(--shadow-soft)]">
        <pre>{children}</pre>
      </div>
    );
  },
  a({ children, href }) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  },
};

export interface MarkdownProps {
  /** The markdown source to render. */
  content: string;
  /** Extra classes on the `.chat-md` prose wrapper. */
  className?: string;
}

export const Markdown = memo(function Markdown({ content, className }: MarkdownProps) {
  return (
    <div className={className ? `chat-md ${className}` : "chat-md"}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
});
